/**
 * Session Store — tracks the current advisor-led session.
 * Persisted to sessionStorage so a tab refresh doesn't lose progress.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  Session,
  Language,
  Goal,
  BudgetRange,
  BedroomPref,
  InvestmentPriority,
  PaymentPlan,
  Unit,
} from '@lib/types';
import type { PriceBreakdown, YieldBreakdown } from '@lib/pricing';
import { useCatalogStore } from '@stores/catalogStore';

/** Payload shape returned by the Worker for a shared session. */
export interface SharePayloadIn {
  session: Session;
  unit: Unit;
  breakdown: PriceBreakdown;
  yieldData: YieldBreakdown;
  language?: Language;
}

/** B.4 (2026-05-08) — start-session payload shape. */
export interface StartSessionInput {
  clientName: string;
  clientPhone: string;
  language: Language;
  advisorId: string;
  advisorName: string;
  advisorRespondId: number;
}

// V3 D4 Phase A — V2 strangler stage tracking. Lives in the same
// store + same sessionStorage entry as v1 so resetSession() clears
// both at once. Array (not Set) for JSON-serialisable persistence.
export type V2Stage =
  | 'welcome'
  | 'location'
  | 'project'
  | 'apartments'
  | 'finance'
  | 'report';

export const V2_STAGE_ORDER: V2Stage[] = [
  'welcome',
  'location',
  'project',
  'apartments',
  'finance',
  'report',
];

// V3 D5 — Finance/Goals slice. Persists with the rest of the v2
// state. Defaults align with Mefteh's Phase F brief (30% down, 15 yr).
export type V2GoalId =
  | 'family-home'
  | 'rental-income'
  | 'capital-growth'
  | 'retirement';

export interface V2FinancePlan {
  downPaymentPct: number; // 10-50
  termYears: number;      // 5-25
}

// V3 D5.7 — selected payment plan from the new investment report.
// Page 6 reads this to fill the Payment summary card; the Worker
// does NOT receive this (the WhatsApp template only carries
// clientName + unitCode + PDF, per audit doc tmp/v1-respond-
// integration.md).
export type V2PaymentPlanId = 'cash' | 'monthly' | 'quarterly' | 'flexible';

interface V2Slice {
  v2Stage: V2Stage;
  v2VisitedStages: V2Stage[];
  v2Goals: V2GoalId[];
  v2PrimaryGoal: V2GoalId | null;
  v2FinancePlan: V2FinancePlan;
  v2PaymentPlanId: V2PaymentPlanId;
  setV2Stage: (stage: V2Stage) => void;
  isV2StageUnlocked: (stage: V2Stage) => boolean;
  toggleV2Goal: (id: V2GoalId) => void;
  setV2PrimaryGoal: (id: V2GoalId | null) => void;
  setV2FinancePlan: (patch: Partial<V2FinancePlan>) => void;
  setV2PaymentPlanId: (id: V2PaymentPlanId) => void;
}

interface SessionState extends Session, V2Slice {
  // actions
  /**
   * B.4 (2026-05-08) — start now takes a curated advisor selection
   * (id + display name + respond.io id) instead of a free-form string.
   */
  startSession: (input: StartSessionInput) => void;
  setEmail: (email: string) => void;
  setGoal: (goal: Goal) => void;
  setBudget: (budget: BudgetRange) => void;
  setBedrooms: (bedrooms: BedroomPref) => void;
  setPriority: (priority: InvestmentPriority) => void;
  selectUnit: (unitCode: string) => void;
  setPaymentPlan: (plan: PaymentPlan) => void;
  recordStepTime: (step: string, ms: number) => void;
  completeSession: (decision: 'reserved' | 'pending' | 'declined') => void;
  resetSession: () => void;
  /**
   * Populate this store + catalogStore.filteredUnits from a payload fetched
   * via /api/sessions/{id}. Used by SharedReport (the QR landing page) so a
   * client viewing a shared link sees the same data the advisor sent.
   */
  hydrateFromShare: (payload: SharePayloadIn) => void;
}

const newSession = (): Session => ({
  sessionId: crypto.randomUUID(),
  startedAt: new Date().toISOString(),
  advisorName: '',
  clientName: '',
  clientPhone: '',
  language: 'ar',
  stepTimes: {},
});

// V2 slice defaults — used by both initial state and resetSession().
const newV2State = (): {
  v2Stage: V2Stage;
  v2VisitedStages: V2Stage[];
  v2Goals: V2GoalId[];
  v2PrimaryGoal: V2GoalId | null;
  v2FinancePlan: V2FinancePlan;
  v2PaymentPlanId: V2PaymentPlanId;
} => ({
  v2Stage: 'welcome',
  v2VisitedStages: ['welcome'],
  v2Goals: [],
  v2PrimaryGoal: null,
  v2FinancePlan: { downPaymentPct: 30, termYears: 15 },
  v2PaymentPlanId: 'monthly',
});

export const useSessionStore = create<SessionState>()(
  persist(
    (set, get) => ({
      ...newSession(),
      ...newV2State(),

      // ── V2 strangler actions ───────────────────────────────
      setV2Stage: (stage) =>
        set((s) => ({
          v2Stage: stage,
          v2VisitedStages: s.v2VisitedStages.includes(stage)
            ? s.v2VisitedStages
            : [...s.v2VisitedStages, stage],
        })),

      isV2StageUnlocked: (stage) => {
        const visited = get().v2VisitedStages;
        if (visited.includes(stage)) return true;
        // Allow the immediate next stage after the latest visited.
        const latest = visited.reduce(
          (max, s) => Math.max(max, V2_STAGE_ORDER.indexOf(s)),
          -1
        );
        return V2_STAGE_ORDER.indexOf(stage) === latest + 1;
      },

      toggleV2Goal: (id) =>
        set((s) => {
          const has = s.v2Goals.includes(id);
          const nextGoals = has
            ? s.v2Goals.filter((g) => g !== id)
            : [...s.v2Goals, id];
          // If the primary goal got removed, demote it.
          const primary =
            s.v2PrimaryGoal && nextGoals.includes(s.v2PrimaryGoal)
              ? s.v2PrimaryGoal
              : nextGoals[0] ?? null;
          return { v2Goals: nextGoals, v2PrimaryGoal: primary };
        }),

      setV2PrimaryGoal: (id) =>
        set((s) => ({
          v2PrimaryGoal: id,
          v2Goals: id && !s.v2Goals.includes(id) ? [...s.v2Goals, id] : s.v2Goals,
        })),

      setV2FinancePlan: (patch) =>
        set((s) => ({ v2FinancePlan: { ...s.v2FinancePlan, ...patch } })),

      setV2PaymentPlanId: (id) => set({ v2PaymentPlanId: id }),

      startSession: ({ clientName, clientPhone, language, advisorId, advisorName, advisorRespondId }) =>
        set({
          ...newSession(),
          clientName,
          clientPhone,
          language,
          advisorId,
          advisorName,
          advisorRespondId,
        }),

      setEmail: (clientEmail) => set({ clientEmail }),
      setGoal: (goal) => set({ goal }),
      setBudget: (budget) => set({ budget }),
      setBedrooms: (bedrooms) => set({ bedrooms }),
      setPriority: (priority) => set({ priority }),
      selectUnit: (selectedUnit) => set({ selectedUnit }),
      setPaymentPlan: (paymentPlan) => set({ paymentPlan }),

      recordStepTime: (step, ms) =>
        set((s) => ({ stepTimes: { ...s.stepTimes, [step]: ms } })),

      completeSession: (decision) =>
        set({ decision, completedAt: new Date().toISOString() }),

      // V3 D4 Phase A — reset clears v1 + v2 fields together so a
      // "new customer" tap drops the user back to a clean splash.
      resetSession: () => set({ ...newSession(), ...newV2State() }),

      hydrateFromShare: ({ session, unit, language }) => {
        set({
          sessionId: session.sessionId,
          startedAt: session.startedAt,
          completedAt: session.completedAt,
          advisorName: session.advisorName,
          advisorId: session.advisorId,
          advisorRespondId: session.advisorRespondId,
          clientName: session.clientName,
          clientPhone: session.clientPhone,
          clientEmail: session.clientEmail,
          language: language ?? session.language ?? 'ar',
          goal: session.goal,
          budget: session.budget,
          bedrooms: session.bedrooms,
          priority: session.priority,
          selectedUnit: session.selectedUnit ?? unit.unitCode,
          paymentPlan: session.paymentPlan,
          decision: session.decision,
          stepTimes: session.stepTimes ?? {},
        });
        // Cross-store hydration so Step6Report.filteredUnits.find(...) succeeds.
        useCatalogStore.getState().setFilteredUnits([unit]);
      },
    }),
    {
      name: 'key2-session',
      storage: {
        getItem: (name) => {
          const v = sessionStorage.getItem(name);
          return v ? JSON.parse(v) : null;
        },
        setItem: (name, value) => sessionStorage.setItem(name, JSON.stringify(value)),
        removeItem: (name) => sessionStorage.removeItem(name),
      },
    }
  )
);
